// dependencies
import React, { useState } from "react";
import { useIntl } from "react-intl";
import { View, StyleSheet } from "react-native";
import { useRouter } from "next/router";

// components
import { Row, Col } from "@cloudspire/legacy-resources/src/components/Grid";
import {
  List,
  ListItem,
} from "@cloudspire/legacy-resources/src/components/List";
import Text from "@cloudspire/legacy-resources/src/components/Text";
import Portal from "@cloudspire/legacy-resources/src/components/Portal";
import Drawer, {
  DrawerHeader,
} from "@cloudspire/legacy-resources/src/components/Drawer";
import SelectCapacity from "@cloudspire/legacy-resources/src/components/SelectCapacity";
import Stack from "@cloudspire/legacy-resources/src/components/Stack";
import Button from "@cloudspire/legacy-resources/src/components/Button2";
import Select from "@cloudspire/legacy-resources/src/components/Select2";
import TextInput from "@cloudspire/legacy-resources/src/components/TextInput2";
import Form from "@cloudspire/legacy-resources/src/components/Form";
import ButtonMap from "@cloudspire/legacy-resources/src/components/ButtonMap";
import { IconSearchO, IconFilterO } from "@cloudspire/legacy-svg-icon-library";

// containers
import AdditionalFilters from "./AdditionalFilters";
import HeadlessDatePickerWithTextInput from "@cloudspire/legacy-resources/src/containers/HeadlessDatePickerWithTextInput";
import AutoCompleteCity from "@cloudspire/legacy-resources/src/containers/AutoCompleteCity";

// constants
import { HOTEL_CATEGORY_INTL_MESSAGES } from "@cloudspire/shared/constants/hotel";

// contexts
import { useRoute } from "@cloudspire/legacy-resources/src/contexts/route";
import { useRouters } from "@cloudspire/legacy-resources/src/contexts/routers";

// hocs
import useViewportMedia from "@cloudspire/legacy-resources/src/hocs/useViewportMedia";

// libraries
import { get, generateUri } from "@cloudspire/legacy-shared/src/libraries";
import { emToPx } from "@cloudspire/legacy-resources/src/libraries";
import {
  fromDate,
  fromString,
} from "@cloudspire/legacy-resources/src/libraries/EnhancedDate";
import stringUppercaseFirst from "@cloudspire/shared/utils/string/uppercaseFirst";

// helpers
import { roomIsAdditionalFilter } from "../../helpers/room";
import { useConfiguration } from "@cloudspire/legacy-resources/src/contexts/configuration";

const styles = StyleSheet.create({
  additionalFilters: {},
});

type PropsType = {
  initialParameterList;
  parameterList;
  total: number;
  showMap: boolean;
  shouldRenderAdditionalFilters;
  onSubmit;
  onPressShowMap;
  onChangeShouldRenderAdditionalFilters;
};

const normalizeParameterList = ({ event, parameterList }) => {
  const newParameterList = {
    ...parameterList,
  };

  if ("checkinCheckout" === event.target.name) {
    const start = event.target.value.start;
    const end = event.target.value.end;

    if (start) {
      newParameterList.checkin = start.format("Y-m-d");
    } else {
      delete newParameterList.checkin;
    }

    if (end) {
      newParameterList.checkout = end.format("Y-m-d");
    } else {
      delete newParameterList.checkout;
    }

    if (start && start.isGreaterThan(end)) {
      newParameterList.checkin = end.format("Y-m-d");
      delete newParameterList.checkout;
    }
  } else if ("city" === event.target.name) {
    newParameterList.city =
      event.target.value?.city ?? event.target.value?.name;
    newParameterList.lat = event.target.value?.lat;
    newParameterList.lng = event.target.value?.lng;
  } else {
    if ("checkbox" === event.target.type) {
      newParameterList[event.target.name] = event.target.checked
        ? event.target.value
        : undefined;
    } else {
      newParameterList[event.target.name] = event.target.value;
    }
  }

  return newParameterList;
};

const RoomListFormContainer: React.FunctionComponent<PropsType> = (props) => {
  const {
    total,
    showMap,
    initialParameterList,
    parameterList: tempParameterList,
    shouldRenderAdditionalFilters,
    onChangeShouldRenderAdditionalFilters,
    onSubmit,
    onPressShowMap,
  } = props;

  const intl = useIntl();
  const route = useRoute();
  const configuration = useConfiguration();
  const { frontRouter } = useRouters();
  const router = useRouter();

  const [parameterList, setParameterList] = useState(initialParameterList);

  const { matches: matchesSm } = useViewportMedia({
    queries: [{ minWidth: 800 }],
  });

  const [additionalFiltersParameterList, setAdditionalFiltersParameterList] =
    useState(parameterList);

  function handlePressMoreFilters() {
    onChangeShouldRenderAdditionalFilters(true);
  }

  function handlePressSearch() {
    onSubmit({ parameterList });
  }

  function handlePressShowMap() {
    onPressShowMap();
  }

  function handlePressResetFilters() {
    if (Object.keys(additionalFiltersParameterList).length > 0) {
      // On supprime les paramètres que s'il existe au moins 1 filtre.
      const newParameterList = {
        ...parameterList,
        page: "1",
      };

      for (const parameterName in additionalFiltersParameterList) {
        if (roomIsAdditionalFilter(parameterName)) {
          delete newParameterList[parameterName];
        }
      }

      setAdditionalFiltersParameterList({});

      setParameterList(newParameterList);

      onSubmit({ parameterList: newParameterList });
    }

    onChangeShouldRenderAdditionalFilters(false);
  }

  function handlePressSearchWithFilters() {
    const newParameterList = {
      ...parameterList,
      ...additionalFiltersParameterList,
      page: "1",
    };

    setParameterList(newParameterList);

    onChangeShouldRenderAdditionalFilters(false);

    onSubmit({ parameterList: newParameterList });
  }

  function handleSubmit(event) {
    event.preventDefault();
    onSubmit({ parameterList });
  }

  function handleChangeMain(event) {
    const { name, value } = event.target;

    if ("city" === name && "hotel" === value?.type) {
      router.push(
        generateUri({
          router: frontRouter,
          name: "App.Action.RoomWithSlug",
          parameters: {
            lang: intl.locale,
            slug: value?.place_id,
          },
        })
      );
    } else {
      setParameterList(normalizeParameterList({ parameterList, event }));
    }
  }

  function handleChangeFilters(event) {
    const { name, value } = event.target;

    if (name === "city" && "hotel" === value?.type) {
      router.push(
        generateUri({
          router: frontRouter,
          name: "App.Action.RoomWithSlug",
          parameters: {
            lang: intl.locale,
            slug: value?.place_id,
          },
        })
      );
    } else {
      setAdditionalFiltersParameterList(
        normalizeParameterList({
          parameterList: additionalFiltersParameterList,
          event,
        })
      );
    }
  }

  const filterCount = Object.keys(additionalFiltersParameterList)
    .filter((filter) => filter)
    .filter(roomIsAdditionalFilter).length;

  return (
    <>
      <Portal selector="body">
        <Drawer
          contentStyle={{ backgroundColor: "#fff", width: "100%" }}
          isOpened={!matchesSm && shouldRenderAdditionalFilters}
          onShouldClose={() => onChangeShouldRenderAdditionalFilters(false)}
        >
          {!matchesSm && shouldRenderAdditionalFilters && (
            <>
              <DrawerHeader
                appearance="classic"
                closePlacement="left"
                onPressClose={() =>
                  onChangeShouldRenderAdditionalFilters(false)
                }
              >
                <div
                  style={{ display: "flex", justifyContent: "space-between" }}
                >
                  <div>
                    {intl.formatMessage({
                      defaultMessage: "Rechercher",
                    })}
                  </div>

                  <Text
                    accessibilityRole="button"
                    style={{ fontWeight: "700" }}
                    onPress={handlePressResetFilters}
                  >
                    {intl.formatMessage({
                      defaultMessage: "Effacer",
                    })}
                  </Text>
                </div>
              </DrawerHeader>

              <Stack marginTop={1} />

              <div
                style={{ paddingLeft: emToPx(0.5), paddingRight: emToPx(0.5) }}
              >
                <AdditionalFilters
                  inline={true}
                  parameterList={additionalFiltersParameterList}
                  onChange={handleChangeFilters}
                />
              </div>

              <div
                style={{
                  position: "sticky",
                  bottom: emToPx(1),
                  display: "flex",
                  justifyContent: "center",
                }}
              >
                <div
                  style={{
                    flex: 1,
                    maxWidth: emToPx(18.75),
                    marginTop: emToPx(2),
                  }}
                >
                  <Button
                    label={intl.formatMessage({
                      defaultMessage: "Rechercher",
                    })}
                    appearance="secondary"
                    onPress={handlePressSearchWithFilters}
                  />
                </div>
              </div>
            </>
          )}
        </Drawer>
      </Portal>
      {matchesSm ? (
        <Form
          action={generateUri({
            router: frontRouter,
            name: route.getName(),
            parameters: route.getParameters(),
            query: tempParameterList,
          })}
          method={"get"}
          onSubmit={handleSubmit}
        >
          <>
            <View style={{ backgroundColor: "#f7f7f7", padding: emToPx(1) }}>
              <Row
                breakpointList={{ md: 1600, lg: Infinity }}
                col={{ md: 8, lg: 8 }}
                gutter={0.5}
              >
                <Col span={{ md: 3, lg: 2 }} offset={{ md: 1, lg: 0 }}>
                  <Row
                    breakpointList={{ all: Infinity }}
                    col={{ all: 2 }}
                    gutter={0.5}
                  >
                    <Col>
                      <AutoCompleteCity
                        name="city"
                        initialValue={get(parameterList, "city", "")}
                        onSelectResult={handleChangeMain}
                      />
                    </Col>

                    <Col>
                      <TextInput
                        name="radius"
                        id="radius"
                        type="number"
                        min={1}
                        step={1}
                        label={intl.formatMessage({
                          defaultMessage: "Rayon",
                        })}
                        placeholder={intl.formatMessage({
                          defaultMessage: "Rayon",
                        })}
                        value={get(parameterList, "radius", "")}
                        onChange={handleChangeMain}
                        onBlur={function (event) {
                          const value = parseFloat(event.target.value);

                          if (!isNaN(value)) {
                            if (value < 1) {
                              setParameterList(function (
                                previousParameterList
                              ) {
                                return {
                                  ...previousParameterList,
                                  radius: 1,
                                };
                              });
                            }
                          }
                        }}
                      />
                    </Col>
                  </Row>
                </Col>

                <Col span={{ md: 4, lg: 2 }}>
                  <HeadlessDatePickerWithTextInput
                    name="checkinCheckout"
                    initialDate={fromDate()}
                    min={fromDate().startOfDays()}
                    start={
                      fromString(
                        get(parameterList, "checkin", null),
                        "Y-m-d"
                      ) || null
                    }
                    end={
                      fromString(
                        get(parameterList, "checkout", null),
                        "Y-m-d"
                      ) || null
                    }
                    onSelect={handleChangeMain}
                  />
                </Col>

                <Col
                  visible={{ md: true, lg: false }}
                  span={{ md: 1, lg: 0 }}
                ></Col>

                <Col span={{ md: 2, lg: 1 }}>
                  <SelectCapacity
                    value={get(parameterList, "capacity", "")}
                    onChange={handleChangeMain}
                  />
                </Col>

                <Col span={{ md: 2, lg: 1 }}>
                  <Select
                    placeholder={intl.formatMessage({
                      defaultMessage: "Type de bien",
                    })}
                    name="category"
                    optionList={configuration.room.categoryNameList.map(
                      (roomCategoryName) => ({
                        label: HOTEL_CATEGORY_INTL_MESSAGES.byValue?.[
                          roomCategoryName
                        ]?.label
                          ? stringUppercaseFirst(
                              intl.formatMessage(
                                HOTEL_CATEGORY_INTL_MESSAGES.byValue[
                                  roomCategoryName
                                ].label
                              )
                            )
                          : roomCategoryName,
                        value: roomCategoryName,
                      })
                    )}
                    native={true}
                    value={get(parameterList, "category", "")}
                    onChange={handleChangeMain}
                  />
                </Col>

                {get(parameterList, "checkin", null) &&
                  get(parameterList, "checkout", null) && (
                    <Col span={{ md: 3 }} visible={{ md: true, lg: false }}>
                      <Row
                        breakpointList={{ all: Infinity }}
                        col={{ all: 2 }}
                        gutter={0.5}
                      >
                        <Col>
                          <TextInput
                            name="minAmount"
                            id="minAmount"
                            type="number"
                            min={1}
                            step={1}
                            label={intl.formatMessage({
                              defaultMessage: "Budget minimum",
                            })}
                            placeholder={intl.formatMessage({
                              defaultMessage: "Budget minimum",
                            })}
                            value={get(parameterList, "minAmount", "")}
                            onChange={handleChangeMain}
                          />
                        </Col>
                        <Col>
                          <TextInput
                            name="maxAmount"
                            id="maxAmount"
                            type="number"
                            min={1}
                            step={1}
                            label={intl.formatMessage({
                              defaultMessage: "Budget maximum",
                            })}
                            placeholder={intl.formatMessage({
                              defaultMessage: "Budget maximum",
                            })}
                            value={get(parameterList, "maxAmount", "")}
                            onChange={handleChangeMain}
                          />
                        </Col>
                      </Row>
                    </Col>
                  )}

                {get(parameterList, "checkin", null) &&
                  get(parameterList, "checkout", null) && (
                    <Col visible={{ md: false, lg: true }}>
                      <TextInput
                        name="minAmount"
                        id="minAmount"
                        type="number"
                        min={1}
                        step={1}
                        label={intl.formatMessage({
                          defaultMessage: "Budget minimum",
                        })}
                        placeholder={intl.formatMessage({
                          defaultMessage: "Budget minimum",
                        })}
                        value={get(parameterList, "minAmount", "")}
                        onChange={handleChangeMain}
                      />
                    </Col>
                  )}

                {get(parameterList, "checkin", null) &&
                  get(parameterList, "checkout", null) && (
                    <Col visible={{ md: false, lg: true }}>
                      <TextInput
                        name="maxAmount"
                        id="maxAmount"
                        type="number"
                        min={1}
                        step={1}
                        label={intl.formatMessage({
                          defaultMessage: "Budget maximum",
                        })}
                        placeholder={intl.formatMessage({
                          defaultMessage: "Budget maximum",
                        })}
                        value={get(parameterList, "maxAmount", "")}
                        onChange={handleChangeMain}
                      />
                    </Col>
                  )}

                {!(
                  get(parameterList, "checkin", null) &&
                  get(parameterList, "checkout", null)
                ) && <Col span={2} />}

                {!shouldRenderAdditionalFilters && (
                  <Col span={{ md: 8, lg: 8 }} offset={{ lg: 0 }}>
                    <Row
                      breakpointList={{ all: Infinity }}
                      col={{ all: 4 }}
                      gutter={0.5}
                    >
                      <Col style={{ justifyContent: "center" }}>
                        {null !== total && (
                          <Text style={{ textAlign: "right" }}>
                            {intl.formatMessage(
                              {
                                defaultMessage:
                                  "{numberOfResults, plural, =0 {aucun résultat} =1 {# résultat} other {# résultats}}",
                              },
                              { numberOfResults: total }
                            )}
                          </Text>
                        )}
                      </Col>

                      <Col>
                        <Button
                          appearance="classic"
                          label={intl.formatMessage({
                            defaultMessage: "Plus de filtres",
                          })}
                          onPress={handlePressMoreFilters}
                        >
                          <div
                            style={{
                              display: "flex",
                              justifyContent: "space-between",
                            }}
                          >
                            <div>
                              <IconFilterO />
                            </div>

                            <div style={{ alignItems: "center" }}>
                              {intl.formatMessage({
                                defaultMessage: "Plus de filtres",
                              })}
                            </div>

                            <div
                              style={{
                                backgroundColor: "#3a485f",
                                paddingRight: "0.1875rem",
                                paddingLeft: "0.1875rem",
                                color: "#ffffff",
                              }}
                            >
                              {filterCount}
                            </div>
                          </div>
                        </Button>
                      </Col>

                      <Col>
                        <ButtonMap
                          opened={showMap}
                          onPress={handlePressShowMap}
                        />
                      </Col>

                      <Col>
                        <Button
                          onPress={handlePressSearch}
                          appearance="secondary"
                          label={intl.formatMessage({
                            defaultMessage: "Rechercher",
                          })}
                        />
                      </Col>
                    </Row>
                  </Col>
                )}
              </Row>
            </View>

            {shouldRenderAdditionalFilters && (
              <>
                <Stack marginTop={1} />

                <AdditionalFilters
                  parameterList={additionalFiltersParameterList}
                  onChange={handleChangeFilters}
                />

                <Stack marginTop={1} />

                <View style={styles.additionalFilters}>
                  <Row
                    breakpointList={{ sm: 1040, md: Infinity }}
                    col={{ sm: 2, md: 6 }}
                    gutter={0.5}
                  >
                    <Col offset={{ sm: 0, md: 4 }} span={{ sm: 1, md: 1 }}>
                      <Button
                        appearance="classic"
                        label={intl.formatMessage({
                          defaultMessage: "Réinitialiser",
                        })}
                        onPress={handlePressResetFilters}
                      />
                    </Col>

                    <Col span={{ sm: 1, md: 1 }}>
                      <Button
                        appearance="secondary"
                        onPress={handlePressSearchWithFilters}
                        label={intl.formatMessage({
                          defaultMessage: "Rechercher",
                        })}
                      />
                    </Col>
                  </Row>
                </View>
              </>
            )}
          </>
        </Form>
      ) : (
        <div
          style={{
            position: "sticky",
            zIndex: 1,
            top: 0,
            backgroundColor: "#ffffff",
            paddingBottom: "0.625rem",
          }}
        >
          <Form
            action={generateUri({
              router: frontRouter,
              name: route.getName(),
              parameters: route.getParameters(),
              query: tempParameterList,
            })}
            method={"get"}
          >
            <>
              {get(parameterList, "city") &&
                get(parameterList, "checkin") &&
                fromString(get(parameterList, "checkin")) &&
                get(parameterList, "checkout") &&
                fromString(get(parameterList, "checkout")) &&
                get(parameterList, "capacity") && (
                  <List
                    inline={true}
                    gutter={1}
                    style={{ alignItems: "center" }}
                  >
                    <ListItem>
                      <Text style={{ fontSize: emToPx(2.625) }}>
                        <IconSearchO />
                      </Text>
                    </ListItem>

                    <ListItem>
                      <List>
                        <ListItem>
                          <Text style={{ fontWeight: "700" }}>
                            {get(parameterList, "city")}
                          </Text>
                        </ListItem>

                        <ListItem>
                          <Text style={{ fontSize: emToPx(0.75) }}>
                            {`${intl.formatDate(
                              fromString(
                                get(parameterList, "checkin")
                              ).getTime()
                            )} — ${intl.formatDate(
                              fromString(
                                get(parameterList, "checkout")
                              ).getTime()
                            )} — ${intl.formatMessage(
                              {
                                defaultMessage:
                                  "{adults, plural, one {1 adulte} other {# adultes}}",
                              },
                              { adults: get(parameterList, "capacity") }
                            )}`}
                          </Text>
                        </ListItem>
                      </List>
                    </ListItem>
                  </List>
                )}

              <Stack marginTop={1} />

              <Row
                breakpointList={{ all: Infinity }}
                col={{ all: 2 }}
                gutter={0.5}
              >
                <Col>
                  <Button
                    label={intl.formatMessage({
                      defaultMessage: "Plus de filtres",
                    })}
                    onPress={() =>
                      onChangeShouldRenderAdditionalFilters(
                        !shouldRenderAdditionalFilters
                      )
                    }
                  >
                    <IconFilterO />
                    &nbsp;
                    {intl.formatMessage({
                      defaultMessage: "Plus de filtres",
                    })}
                  </Button>
                </Col>

                <Col>
                  <ButtonMap opened={showMap} onPress={handlePressShowMap} />
                </Col>
              </Row>
            </>
          </Form>
        </div>
      )}
    </>
  );
};

export default RoomListFormContainer;
